/**
 * Provides Agora call state to the entire game tree.
 *
 * When AV_CONFIG.USE_JOIN_SHEET is false (default), the provider
 * auto-joins the Agora channel in receive-only mode so every player
 * can see/hear broadcasters without explicitly joining a call.
 *
 * On gameId/userId change or when leaving (no gameId), we leave the channel
 * and presence so the device releases mic/camera and the green indicator clears.
 *
 * Wrap this around the game content so both AVButton
 * and PlayerContainer (for video avatars) can access the same state.
 */

import React, { useEffect, useRef } from 'react'
import { useAction, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { useAgoraCall, AVProvider } from '../../hooks/av'
import { useGameContext } from '../../screens/GameContainer/GameContext'
import { useGameState } from '../../screens/GameContainer/useGameState'
import { AV_CONFIG } from '../../hooks/av/config'
import { rootStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'

// Solo modes against bots have nobody to talk to, so skip the Agora channel.
const NO_AV_GAME_MODES = new Set(['daily10', 'practice'])

export function AVCallProvider({ children }: { children: React.ReactNode }) {
  const [state, actions] = useAgoraCall()
  const { gameId, userId } = useGameContext()
  const { game } = useGameState()
  // Wait for the game to load so we know its mode before joining.
  const avEnabled = game ? !NO_AV_GAME_MODES.has(game.mode) : false
  const generateToken = useAction(api.av.actions.generateAgoraToken)
  const leavePresence = useMutation(api.av.presence.leave)
  const autoJoinAttempted = useRef(false)
  const prevGameRef = useRef<{ gameId: Id<'games'>; userId: string } | null>(null)

  // When gameId/userId change or become empty: leave channel + presence so mic/camera and green indicator clear
  useEffect(() => {
    if (AV_CONFIG.USE_JOIN_SHEET) return

    const prev = prevGameRef.current
    const hasCurrentGame = Boolean(gameId && userId)
    const gameChanged = prev !== null && (prev.gameId !== gameId || prev.userId !== userId)
    const shouldLeave = gameChanged || !hasCurrentGame
    const hadConnection = state.isConnected || autoJoinAttempted.current

    if (shouldLeave && prev !== null && hadConnection) {
      leavePresence({ gameId: prev.gameId, userId: prev.userId }).catch(() => {})
      actions.leave()
      autoJoinAttempted.current = false
    }

    const currentGame = gameId && userId ? { gameId, userId } : null
    prevGameRef.current = currentGame

    const gameForCleanup = currentGame
    return () => {
      if (gameForCleanup !== null && hadConnection) {
        leavePresence({ gameId: gameForCleanup.gameId, userId: gameForCleanup.userId }).catch(() => {})
        actions.leave()
      }
    }
  }, [gameId, userId, state.isConnected, actions, leavePresence])

  // Auto-join when we have a game and are not connected
  useEffect(() => {
    if (AV_CONFIG.USE_JOIN_SHEET) return
    if (!gameId) return
    if (!userId) return
    if (!avEnabled) return
    if (state.isConnected) return
    if (autoJoinAttempted.current) return

    autoJoinAttempted.current = true

    let cancelled = false
    const connectToChannel = async () => {
      try {
        const { token, channelName, uid } = await generateToken({ gameId, userId })
        if (cancelled) return
        actions.join(token, channelName, uid, 'off')
        rootStore?.analytics.setEvent(events.av.channel_auto_joined, {
          gameId, isConvexEnabled: true
        })
      } catch (err) {
        console.error('[AVCallProvider] auto-join failed', err)
        autoJoinAttempted.current = false
      }
    }
    connectToChannel()

    return () => {
      cancelled = true
    }
  }, [gameId, userId, avEnabled, state.isConnected, generateToken, actions])

  return (
    <AVProvider state={state} actions={actions}>
      {children}
    </AVProvider>
  )
}
